import { useMemo } from "react";
import { Link, getRouteApi } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { unwrap } from "@tern/api-client";
import { PageHeader } from "@/components/AppShell";
import { ProjectIcon } from "@/components/ProjectIcon";
import { EmptyState } from "@/components/EmptyState";
import { IssueRow } from "@/components/IssueRow";
import { api } from "@/lib/api";
import { sortIssues } from "@/lib/issues";
import { useProjectStore, type ProjectData } from "@/lib/store";
import { DEFAULT_VIEW } from "@/lib/views";

const route = getRouteApi("/$ws/my-issues/$tab");
const TABS = ["assigned", "created", "subscribed", "activity"] as const;

export function MyIssuesPage() {
  const { t } = useTranslation();
  const { ws, tab } = route.useParams();
  const q = useQuery({
    queryKey: ["my-issues", ws, tab],
    queryFn: async () => unwrap(await api.GET("/api/workspaces/{slug}/my-issues/{tab}", { params: { path: { slug: ws, tab } } })),
    refetchInterval: 30_000,
  });
  // Prefer live store data for projects already loaded; otherwise build a view-only bundle.
  const loaded = useProjectStore((s) => s.byId);
  const data = q.data;
  const bundles = useMemo(() => {
    const out: { d: ProjectData; issues: NonNullable<typeof data>["issues"] }[] = [];
    if (!data) return out;
    for (const p of data.projects) {
      const live = loaded[p.id];
      const d: ProjectData = live ?? {
        project: p,
        states: Object.fromEntries(data.states.filter((s) => s.project_id === p.id).map((s) => [s.id, s])),
        labels: Object.fromEntries(data.labels.filter((l) => l.project_id === p.id).map((l) => [l.id, l])),
        members: [],
        users: Object.fromEntries(data.users.map((u) => [u.id, u])),
        issues: {},
        relations: {},
        tasks: {},
        dependencies: {},
        health: {},
        milestones: {},
        version: 0,
        slug: ws,
      };
      const issues = data.issues.filter((i) => i.project_id === p.id).map((i) => live?.issues[i.id] ?? i);
      out.push({ d, issues: sortIssues(issues, "priority") });
    }
    return out;
  }, [data, loaded, ws]);

  const view = { ...DEFAULT_VIEW, groupBy: "none" as const };
  const total = q.data?.issues.length ?? 0;

  return (
    <>
      <PageHeader crumbs={[t("myIssues.title")]} />
      <div className="flex h-10 items-center gap-1 border-b border-border px-3">
        {TABS.map((k) => (
          <Link key={k} to="/$ws/my-issues/$tab" params={{ ws, tab: k }} className={`rounded-md px-2.5 py-1 text-[13px] ${tab === k ? "bg-active text-fg" : "text-muted hover:bg-hover hover:text-fg"}`}>
            {t(`myIssues.tabs.${k}`)}
          </Link>
        ))}
      </div>
      {q.isPending ? (
        <div className="flex flex-1 items-center justify-center text-[13px] text-muted">{t("common.loading")}</div>
      ) : total === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <EmptyState title={t("myIssues.emptyTitle")} body={t(`myIssues.empty.${tab}`)} />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {bundles.map(({ d, issues }) => (
            <section key={d.project.id}>
              <header className="sticky top-0 z-10 flex h-9 items-center gap-2 border-b border-border bg-sidebar/95 px-3 text-[13px] backdrop-blur">
                <ProjectIcon project={d.project} size={16} />
                <Link to="/$ws/project/$key" params={{ ws, key: d.project.key }} className="font-medium hover:underline">{d.project.name}</Link>
                <span className="text-muted">{issues.length}</span>
              </header>
              {issues.map((i) => (
                <IssueRow key={i.id} d={d} issue={i} view={view} showProject />
              ))}
            </section>
          ))}
        </div>
      )}
    </>
  );
}
